import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  applyBrandAccent,
  clearBrandAccent,
  type BrandAccent,
} from '@/data/brandSwatches';
import { patchUserPreferences } from '@/api/settings';
import { AuthContext } from './AuthContext';

/**
 * Theme preference: explicit light/dark, or 'system' which follows the OS.
 */
export type ThemePref = 'light' | 'dark' | 'system';

/**
 * Resolved theme actually applied to <html data-theme>. 'system' is collapsed
 * to either 'light' or 'dark' based on the current matchMedia state.
 */
export type ThemeResolved = 'light' | 'dark';

const STORAGE_KEY = 'pcgop:theme';
const TRANSITION_CLASS = 'theme-transition';
const TRANSITION_MS = 250;

/**
 * Mirrors the inline bootstrap script in index.html. Exported so the two
 * stay in sync — if you edit one, edit the other (or import this string in
 * a build step).
 */
export const THEME_BOOTSTRAP_SCRIPT = `(function () {
  try {
    var pref = localStorage.getItem('${STORAGE_KEY}');
    var resolved;
    if (pref === 'light' || pref === 'dark') {
      resolved = pref;
    } else {
      resolved = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light';
    }
    document.documentElement.setAttribute('data-theme', resolved);
  } catch (e) {
    document.documentElement.setAttribute('data-theme', 'dark');
  }
})();`;

interface ThemeContextValue {
  /** User's stored preference (light/dark/system). */
  preference: ThemePref;
  /** The theme actually applied to <html>. Always light or dark. */
  resolved: ThemeResolved;
  /** Set a new preference; persists to localStorage and applies to <html>. */
  setPreference: (pref: ThemePref) => void;
  /** Cycle preference: light -> dark -> system -> light. */
  cyclePreference: () => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

function readStoredPref(): ThemePref {
  if (typeof window === 'undefined') return 'system';
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    if (v === 'light' || v === 'dark' || v === 'system') return v;
  } catch {
    /* ignore */
  }
  return 'system';
}

function getSystemTheme(): ThemeResolved {
  if (typeof window === 'undefined' || !window.matchMedia) return 'dark';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function resolvePref(pref: ThemePref): ThemeResolved {
  return pref === 'system' ? getSystemTheme() : pref;
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePref>(() => readStoredPref());
  const [resolved, setResolved] = useState<ThemeResolved>(() => resolvePref(readStoredPref()));
  const transitionTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Subscribe to AuthContext via the raw context (not the `useAuth` hook).
   * `useAuth` throws when not inside an AuthProvider, but ThemeProvider may
   * be mounted as an ancestor of AuthProvider (current main.tsx ordering).
   * Using `useContext(AuthContext)` with an undefined-tolerant read lets the
   * theme system degrade gracefully to localStorage-only when auth state
   * isn't available.
   */
  const auth = useContext(AuthContext);
  const user = auth?.user ?? null;
  const serverThemePref = user?.theme_preference;
  const tenantBrandAccent: BrandAccent | undefined = user?.tenant_brand_accent;

  /** Track the last theme_preference we PATCHed so we don't echo the same value back. */
  const lastSyncedPref = useRef<ThemePref | undefined>(undefined);
  /** Track the brand accent currently applied to <html> so we can re-apply on theme flip. */
  const appliedBrandRef = useRef<BrandAccent | null>(null);

  /**
   * Apply the resolved theme to <html> with a transient transition class.
   * The class is removed after TRANSITION_MS so non-theme interactions stay
   * snappy (no transition delay on hover, focus, etc.).
   */
  const applyTheme = useCallback((next: ThemeResolved, animate: boolean) => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;

    if (animate) {
      root.classList.add(TRANSITION_CLASS);
      if (transitionTimer.current) clearTimeout(transitionTimer.current);
      transitionTimer.current = setTimeout(() => {
        root.classList.remove(TRANSITION_CLASS);
        transitionTimer.current = null;
      }, TRANSITION_MS);
    }

    root.setAttribute('data-theme', next);
    setResolved(next);
  }, []);

  /**
   * Apply or clear the tenant's brand accent. Pass `null` to remove the
   * inline overrides (falls back to the values declared in tokens.css).
   */
  const applyTenantBrand = useCallback(
    (accent: BrandAccent | null, themeResolved: ThemeResolved) => {
      if (accent) {
        applyBrandAccent(accent, themeResolved);
        appliedBrandRef.current = accent;
      } else {
        clearBrandAccent();
        appliedBrandRef.current = null;
      }
    },
    []
  );

  // Sync resolved theme on first mount (in case bootstrap script and React
  // disagree, or hydration replaced data-theme).
  useEffect(() => {
    const next = resolvePref(preference);
    applyTheme(next, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen to OS theme changes only when preference is 'system'.
  useEffect(() => {
    if (preference !== 'system') return;
    if (typeof window === 'undefined' || !window.matchMedia) return;

    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => {
      applyTheme(e.matches ? 'dark' : 'light', true);
    };
    // Modern browsers: addEventListener; Safari < 14: addListener.
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    }
    mq.addListener(handler);
    return () => mq.removeListener(handler);
  }, [preference, applyTheme]);

  /**
   * Adopt the server-stored theme_preference once it arrives from /auth/me.
   * Only fires when the server value differs from local state — avoids
   * re-PATCHing on every user load. Skipped entirely when `theme_preference`
   * is undefined (backend hasn't shipped yet → falls back to localStorage).
   */
  useEffect(() => {
    if (!serverThemePref) return;
    // Mark as "already synced" so our setPreference flush doesn't echo it back.
    lastSyncedPref.current = serverThemePref;
    if (serverThemePref === preference) return;
    setPreferenceState(serverThemePref);
    try {
      if (serverThemePref === 'system') {
        window.localStorage.removeItem(STORAGE_KEY);
      } else {
        window.localStorage.setItem(STORAGE_KEY, serverThemePref);
      }
    } catch {
      /* ignore quota / private mode */
    }
    applyTheme(resolvePref(serverThemePref), true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverThemePref]);

  /**
   * Apply or clear the tenant brand accent whenever the user's
   * tenant_brand_accent changes — including login (user becomes non-null
   * with an accent) and logout (user becomes null → clear).
   */
  useEffect(() => {
    if (!user) {
      applyTenantBrand(null, resolved);
      return;
    }
    if (tenantBrandAccent) {
      applyTenantBrand(tenantBrandAccent, resolved);
    } else {
      applyTenantBrand(null, resolved);
    }
    // `resolved` is intentionally omitted here — the next effect handles
    // re-applying on theme flips so we don't double-apply.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, tenantBrandAccent, applyTenantBrand]);

  /**
   * When the resolved theme flips (light↔dark) and a brand accent is
   * currently applied, re-apply it so the new theme's swatch values land.
   */
  useEffect(() => {
    if (appliedBrandRef.current) {
      applyBrandAccent(appliedBrandRef.current, resolved);
    }
  }, [resolved]);

  const setPreference = useCallback(
    (pref: ThemePref) => {
      setPreferenceState(pref);
      try {
        if (pref === 'system') {
          window.localStorage.removeItem(STORAGE_KEY);
        } else {
          window.localStorage.setItem(STORAGE_KEY, pref);
        }
      } catch {
        /* ignore quota / private mode */
      }
      applyTheme(resolvePref(pref), true);

      // Fire-and-forget PATCH to /auth/me/preferences. Only when:
      //   1. user is logged in (we have an auth context with a user), and
      //   2. the new value differs from the last value we synced (skip no-ops).
      // Network failures are swallowed — the local state already reflects
      // the change, and the next /auth/me load will surface the truth.
      if (user && lastSyncedPref.current !== pref) {
        lastSyncedPref.current = pref;
        void patchUserPreferences({ theme_preference: pref }).catch(() => {
          /* ignore — UI never blocks on this */
        });
      }
    },
    [applyTheme, user]
  );

  const cyclePreference = useCallback(() => {
    setPreference(
      preference === 'light' ? 'dark' : preference === 'dark' ? 'system' : 'light'
    );
  }, [preference, setPreference]);

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      if (transitionTimer.current) clearTimeout(transitionTimer.current);
    };
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ preference, resolved, setPreference, cyclePreference }),
    [preference, resolved, setPreference, cyclePreference]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return ctx;
}
